import React, { useCallback, useEffect, useState } from "react";
import { Row, Col, Card, Descriptions, Space, Button, Typography, Divider, Tag, Tooltip, Modal, Table, Alert, Input, Select } from "antd";
import { CalendarOutlined, CreditCardOutlined, DollarOutlined, EditOutlined, EyeInvisibleOutlined, EyeOutlined, PlusCircleTwoTone, QuestionCircleOutlined, ReloadOutlined, RetweetOutlined, UsergroupAddOutlined } from "@ant-design/icons";
import HtmlParser from "react-html-parser";
import { renderGender, renderAppointmentName, isValidDate, renderPaymentMethods, repeatPhoneNumber, renderMoney } from "@/utils/helpers";
import { checkPermission } from "@/utils/permission";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { executeCustomerFinancialRepair, fetchCustomer, previewCustomerFinancialRepair, reconcileCustomerDebt } from "@/redux/actions/customerActions";
import { NotificationManager } from "react-notifications";
import CustomerRelationshipsTable from "@/components/customers/CustomerRelationshipsTable";
import HandlePhoneNumber from "@/components/HandlePhoneNumber";
import PrintButton from "@/components/PrintButton/PrintButton";
import PrintCustomerProfile from "@/components/print-templates/PrintCustomerProfile";
import { getByCustomer } from "@/redux/actions/medicalRecordActions";
import PrintSurgeryCommitment from "@/components/print-templates/PrintSurgeryCommitment";
import PrintRepresentative from "@/components/print-templates/PrintRepresentative";
import PrintRefusa from "@/components/print-templates/PrintRefusa";
import CreatedAtCell from "@/components/CreatedAtCell";
import CustomerEditForm from "@/components/customers/CustomerEditForm";
import CustomerPrimaryPhoneModal from "@/components/customers/CustomerPrimaryPhoneModal";
import CustomerPaymentList from "@/components/customers/CustomerPaymentList";
import AdditionalCost from "@/components/customers/AdditionalCost";
import CustomerFinanceActions from "@/components/customers/CustomerFinanceActions";
import moment from "moment";
import { isMobile } from "react-device-detect";
import {
    financialRepairModeOptions,
    formatFinancialRepairAction,
    formatFinancialRepairBlocker,
    formatFinancialRepairDirection,
    formatFinancialRepairIssue,
    formatFinancialRepairStatus,
    formatReceiptItemIssueReason,
    formatReceiptItemType,
    getFinancialRepairAlertType,
    getFinancialRepairStatusColor,
} from "./customerFinancialRepairPresentation";

const { Text, Paragraph } = Typography;

const FinanceLabel = ({ label, description }) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        <span>{label}</span>
        <Tooltip placement="topLeft" title={description}>
            <QuestionCircleOutlined
                style={{ color: "#8c8c8c", fontSize: 13, cursor: "help" }}
            />
        </Tooltip>
    </span>
);

const debtConflictLabels = {
    customer_id: "Khách hàng",
    receipt_id: "Phiếu thu",
    receipt_code: "Mã phiếu thu",
    amount_paid: "Tiền đã thu",
    total_paid: "Tổng tiền đã thu",
    refunded_amount: "Đã hoàn",
    allocated_total: "Đã phân bổ",
    balance_amount: "Số dư",
    deposit_balance: "Tiền cọc",
    transferred_in_amount: "Chuyển đến",
    transferred_out_amount: "Chuyển đi",
    deposit_adjustment_net: "Điều chỉnh tiền cọc ròng",
    financial_adjustment_debt: "Công nợ điều chỉnh",
    funding_difference: "Chênh lệch nguồn ròng",
    unaccounted_amount: "Chưa xác định nguồn",
    overaccounted_amount: "Ghi nhận vượt nguồn",
    debt: "Công nợ hiện tại",
    expected_debt: "Công nợ dự kiến",
    service_outstanding_amount: "Công nợ dịch vụ",
    product_outstanding_amount: "Công nợ sản phẩm",
    reason: "Nguyên nhân",
};

export default function CustomerInfoTab(props) {
    const dispatch = useDispatch();
    const location = useLocation();

    // ===== DATA =====
    const customer = props?.data ?? {};
    const customerId = customer?.id;

    const phoneList = Array.isArray(customer?.phones) ? customer.phones : [];
    const relationshipList = Array.isArray(customer?.relationships) ? customer.relationships : [];
    const latestAppointment = (customer?.latest_appointment && typeof customer.latest_appointment === 'object')
        ? customer.latest_appointment
        : {};

    // ===== UI STATES =====
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isReconcilingDebt, setIsReconcilingDebt] = useState(false);
    const [debtReconciliationBlocker, setDebtReconciliationBlocker] = useState(null);
    const [financialRepairPreview, setFinancialRepairPreview] = useState(null);
    const [financialRepairResult, setFinancialRepairResult] = useState(null);
    const [financialRepairReason, setFinancialRepairReason] = useState("");
    const [financialRepairMode, setFinancialRepairMode] = useState("AUTO_REPAIR");
    const [isLoadingFinancialRepair, setIsLoadingFinancialRepair] = useState(false);

    const [openPrimaryPhoneModal, setOpenPrimaryPhoneModal] = useState(false);
    const [openPaymentMethodModal, setOpenPaymentMethodModal] = useState(false);
    const [openAdditionalCostModal, setOpenAdditionalCostModal] = useState(false);
    const [openProductPaymentModal, setOpenProductPaymentModal] = useState(false);

    const [medicalRecord, setMedicalRecord] = useState([]);
    const [isPrintDisabled, setIsPrintDisabled] = useState(false);

    // ===== PERMISSIONS =====
    const canUpdateCustomer = checkPermission("customer", "update");
    const canViewFinance = checkPermission("customer", "view_finance");
    const canViewPhoneNumber = checkPermission("customer", "phonenumber");
    const canInputPinCode = checkPermission("customer", "input_pin_code");

    // ===== FINANCE UI =====
    const [isFinanceVisible, setIsFinanceVisible] = useState(canViewFinance);

    useEffect(() => {
        if (!canViewFinance) setIsFinanceVisible(false);
    }, [canViewFinance]);

    // ===== HANDLERS (chuẩn hoá tên) =====
    const openEditModal = () => setIsEditModalOpen(true);
    const closeEditModal = () => setIsEditModalOpen(false);

    const handleRefreshCustomer = async () => {
        try {
            setIsRefreshing(true);

            // giữ nguyên logic cũ:
            // ưu tiên reloadData từ cha, fallback fetchCustomer
            if (typeof props.reloadData === "function") {
                await props.reloadData();
            } else if (customerId) {
                await dispatch(fetchCustomer(customerId));
            }

            // console.log("Customer refreshed");
        } catch (error) {
            console.error("Failed to refresh customer:", error);
        } finally {
            setIsRefreshing(false);
        }
    };

    const loadMedicalRecords = useCallback(async () => {
        if (!customerId) return;

        setIsPrintDisabled(true);
        try {
            const result = await dispatch(getByCustomer({}, customerId));
            const data = result?.data;

            if (data && Object.keys(data).length > 0) {
                setMedicalRecord({ ...data });
            } else {
                setMedicalRecord([]);
            }
        } catch (error) {
            console.error("Failed to load medical records:", error);
            setMedicalRecord([]);
        } finally {
            setIsPrintDisabled(false);
        }
    }, [dispatch, customerId]);

    // giữ nguyên “ý tưởng cũ”: load theo page/route change,
    // nhưng an toàn hơn: cũng reload khi customerId đổi
    useEffect(() => {
        // nếu bạn muốn tắt như code cũ thì comment dòng dưới
        // loadMedicalRecords();
    }, [location.pathname, loadMedicalRecords]);

    // ===== DERIVED VALUES =====
    const isNoPhone = customer?.no_phone === 1;

    const financialOverview = customer?.financial_overview || {};
    const debtReconciliation = customer?.debt_reconciliation || {};
    const productOutstanding = Math.max(0, Number(financialOverview.product_outstanding_amount || 0));
    const totalRevenue = Math.max(0, Number(financialOverview.total_revenue_amount || 0));
    const totalPaid = Math.max(0, Number(financialOverview.total_paid_amount || 0));
    const totalDebt = Math.max(0, Number(financialOverview.total_outstanding_amount || 0));
    const productPaymentItems = Array.isArray(customer?.product_payment_items)
        ? customer.product_payment_items
        : [];

    // Không dùng tổng tiền khách đã nộp vì số này có thể còn ở số dư hoặc tiền cọc.
    const paidPercent = Math.max(0, Number(financialOverview.paid_percent || 0));
    const canReconcileDebt = canUpdateCustomer && Boolean(debtReconciliation.needs_reconciliation);

    const handleReconcileDebt = () => {
        if (!customerId || isReconcilingDebt) return;
        Modal.confirm({
            title: "Xác nhận cập nhật công nợ",
            content: "Hệ thống sẽ đối soát tiền đã nộp và chỉ bổ sung phần còn thiếu vào đúng dịch vụ/sản phẩm. Các phân bổ đúng sẽ được giữ nguyên.",
            okText: "Cập nhật công nợ",
            cancelText: "Hủy",
            maskClosable: false,
            width: isMobile ? "100%" : "40%",
            onOk: async () => {
                setIsReconcilingDebt(true);
                try {
                    const result = await dispatch(reconcileCustomerDebt(customerId));
                    NotificationManager.success(result?.message || "Cập nhật công nợ thành công.");
                    if (typeof props.reloadData === "function") await props.reloadData();
                    else await dispatch(fetchCustomer(customerId));
                } catch (error) {
                    const response = error?.response?.data || {};
                    const blocked = response?.data || {};
                    const hasInvalidMappings = Array.isArray(blocked.invalid_items)
                        && blocked.invalid_items.length > 0;
                    const hasBusinessBlocker = hasInvalidMappings
                        || Boolean(blocked.block_code)
                        || (Array.isArray(blocked.required_actions) && blocked.required_actions.length > 0);
                    if (hasBusinessBlocker) {
                        setDebtReconciliationBlocker(blocked);
                        return;
                    }
                    NotificationManager.error(response?.msg || "Không thể cập nhật công nợ.");
                    throw error;
                } finally {
                    setIsReconcilingDebt(false);
                }
            },
        });
    };

    const openFinancialRepair = async () => {
        if (!customerId || isLoadingFinancialRepair) return;
        setIsLoadingFinancialRepair(true);
        setFinancialRepairResult(null);
        try {
            const preview = await dispatch(previewCustomerFinancialRepair(customerId));
            setFinancialRepairPreview(preview);
            setFinancialRepairMode(preview?.recommended_mode || "AUTO_REPAIR");
        } catch (error) {
            NotificationManager.error(error?.response?.data?.msg || "Không thể kiểm tra dữ liệu tài chính.");
        } finally {
            setIsLoadingFinancialRepair(false);
        }
    };

    const executeFinancialRepair = async () => {
        if (!customerId || !financialRepairPreview?.plan_hash || isLoadingFinancialRepair) return;
        setIsLoadingFinancialRepair(true);
        try {
            const result = await dispatch(executeCustomerFinancialRepair(customerId, {
                plan_hash: financialRepairPreview.plan_hash,
                mode: financialRepairMode,
                reason: financialRepairReason.trim()
            }));
            setFinancialRepairResult(result);
            NotificationManager.success(result?.message || "Đã xử lý dữ liệu tài chính.");
            if (typeof props.reloadData === "function") await props.reloadData();
            else await dispatch(fetchCustomer(customerId));
        } catch (error) {
            NotificationManager.error(error?.response?.data?.msg || "Không thể xử lý dữ liệu tài chính.");
        } finally {
            setIsLoadingFinancialRepair(false);
        }
    };

    let collectRateColor = "#1677ff";
    if (paidPercent < 50) collectRateColor = "#fa8c16";
    else if (paidPercent < 100) collectRateColor = "#531dab";

    const customerStatusName = customer?.customer_status?.name || "—";
    const customerStatusColor = customer?.customer_status?.text_color;
    const financialRepairDisplayStatus = financialRepairResult?.status || financialRepairPreview?.status;

    return (
        <div>
            {/* ACTION BAR */}
            <div className="mt-4 mb-4">
                <Row gutter={8}>
                    <Col>
                        <Space size={8}>
                            <Button
                                type="primary"
                                icon={<EditOutlined />}
                                onClick={openEditModal}
                                disabled={!canUpdateCustomer}
                            >
                                Cập nhật thông tin khách hàng
                            </Button>

                            {/* PRINTS */}
                            <PrintButton
                                disabled={isPrintDisabled}
                                printText="In bệnh án"
                                tooltipText="Bệnh án ngoại trú răng miệng"
                            >
                                <PrintCustomerProfile customer={customer} data={medicalRecord} />
                            </PrintButton>

                            <PrintButton
                                disabled={isPrintDisabled}
                                printText="In giấy cam kết"
                                tooltipText="Giấy cam kết chấp thuận phẫu thuật, thủ thuật và gây mê hồi sức"
                            >
                                <PrintSurgeryCommitment customer={customer} data={{}} />
                            </PrintButton>

                            <PrintButton
                                disabled={isPrintDisabled}
                                printText="In giấy đại diện"
                                tooltipText="Giấy đề nghị làm người đại diện cho người bệnh"
                            >
                                <PrintRepresentative customer={customer} data={{}} />
                            </PrintButton>

                            <PrintButton
                                disabled={isPrintDisabled}
                                printText="In giấy từ chối"
                                tooltipText="Giấy cam kết từ chối sử dụng dịch vụ khám bệnh, chữa bệnh"
                            >
                                <PrintRefusa customer={customer} data={{}} />
                            </PrintButton>

                            <Button
                                onClick={handleRefreshCustomer}
                                icon={<ReloadOutlined />}
                                loading={isRefreshing}
                            >
                                Refresh
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </div>

            <Row gutter={[16, 16]}>
                <Col xs={24} md={24} lg={14}>
                    {/* BASIC INFO */}
                    <Card size="small" bordered style={{ marginBottom: 16 }}>
                        <Descriptions column={1} size="small" bordered={false}>
                            <Descriptions.Item label="Họ tên">
                                <strong>{customer?.full_name || ""}</strong>
                            </Descriptions.Item>

                            <Descriptions.Item label="Nickname">{customer?.fb_name || ""}</Descriptions.Item>

                            <Descriptions.Item label="Mã số sổ khám">
                                {customer?.medical_book_number ? (
                                    <Paragraph
                                        style={{ margin: 0 }}
                                        copyable={{
                                            text: customer?.medical_book_number ?? null,
                                            tooltips: ["Sao chép mã", "Đã sao chép!"],
                                        }}
                                    >
                                        {customer?.medical_book_number}
                                    </Paragraph>
                                ) : (
                                    "-"
                                )}
                            </Descriptions.Item>

                            <Descriptions.Item label="Mã PID">
                                {customer?.invisalignId ? (
                                    <Paragraph
                                        style={{ margin: 0 }}
                                        copyable={{
                                            text: customer?.invisalignId ?? null,
                                            tooltips: ["Sao chép mã", "Đã sao chép!"],
                                        }}
                                    >
                                        {customer?.invisalignId}
                                    </Paragraph>
                                ) : (
                                    "-"
                                )}
                            </Descriptions.Item>

                            <Descriptions.Item label="Labo">
                                {customer?.labo_names ? (
                                    <>{customer?.labo_names}</>
                                ) : (
                                    "-"
                                )}
                            </Descriptions.Item>

                            <Descriptions.Item label="Giới tính">
                                {customer ? renderGender(customer.gender) : null}
                            </Descriptions.Item>

                            <Descriptions.Item label="Năm sinh / Tuổi">
                                {customer?.birthday ? (
                                    <Space>
                                        <>{isValidDate(customer?.birthday, false)}</>
                                        <Text type="secondary">•</Text>
                                        <>{customer?.age ? `${customer?.age} tuổi` : "—"}</>
                                    </Space>
                                ) : (
                                    "—"
                                )}
                            </Descriptions.Item>

                            <Descriptions.Item label="Số điện thoại">
                                {isNoPhone ? (
                                    <span className="color-gray">{customer?.other_contact}</span>
                                ) : phoneList.length > 0 ? (
                                    <>
                                        {phoneList.map((item, idx) => (
                                            <span key={item.customer_id + "-" + idx}>
                                                <HandlePhoneNumber
                                                    isPrimary={item.is_primary}
                                                    showIsPrimary={phoneList.length > 1}
                                                    permission={canViewPhoneNumber}
                                                    inputPinCode={canInputPinCode}
                                                    customer_id={item.customer_id}
                                                    hiddenPhoneNumber={repeatPhoneNumber(item.phone)}
                                                    fullPhoneNumber={item.phone}
                                                />
                                                {idx < phoneList.length - 1 && <>&nbsp;-&nbsp;</>}
                                            </span>
                                        ))}

                                        {phoneList.length > 1 && (
                                            <>
                                                <Divider type="vertical" />
                                                <Button
                                                    size="small"
                                                    icon={<EditOutlined />}
                                                    onClick={() => setOpenPrimaryPhoneModal(true)}
                                                    type="dashed"
                                                    className="color-blue"
                                                >
                                                    Cập nhật số chính
                                                </Button>
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <span className="color-gray">{customer?.other_contact}</span>
                                )}
                            </Descriptions.Item>

                            <Descriptions.Item label="Địa chỉ">
                                {customer?.address || "-"}, {customer?.hamlet || "-"}, {customer?.ward?.name || "-"},{" "}
                                {customer?.province?.name || "-"}
                            </Descriptions.Item>

                            <Descriptions.Item label="Cơ sở">{customer?.branch?.name || "-"}</Descriptions.Item>

                            <Descriptions.Item label="Nguồn khách hàng">
                                {customer?.customer_source?.name || "—"}
                            </Descriptions.Item>

                            <Descriptions.Item label="Ghi chú y khoa">
                                {customer?.condition_names_string && (
                                    <Text type="danger">
                                        <PlusCircleTwoTone twoToneColor="red" />
                                        &nbsp;{customer?.condition_names_string}
                                    </Text>
                                )}
                            </Descriptions.Item>

                            <Descriptions.Item label="Ghi chú">
                                {customer?.note ? HtmlParser(customer.note) : "-"}
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>
                    {/* LATEST APPOINTMENT */}
                    <Card
                        size="small"
                        title={
                            <>
                                <CalendarOutlined /> Lịch hẹn gần nhất
                            </>
                        }
                        bordered
                        style={{ marginBottom: 16 }}
                    >
                        <Descriptions column={1} size="small" bordered={false}>
                            <Descriptions.Item label="Ngày giờ">
                                {latestAppointment?.scheduled_at ? isValidDate(latestAppointment.scheduled_at, true) : "—"}
                            </Descriptions.Item>

                            <Descriptions.Item label="Nội dung">
                                <strong>{renderAppointmentName(latestAppointment.name)}</strong>
                            </Descriptions.Item>

                            <Descriptions.Item label="Ghi chú">
                                {latestAppointment?.note ? HtmlParser(latestAppointment.note) : "—"}
                            </Descriptions.Item>

                            <Descriptions.Item label="Địa điểm">
                                {latestAppointment?.branch?.name || "—"}
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>
                    {/* INTERACTION */}
                    <Card
                        size="small"
                        bordered
                        style={{ marginBottom: 16, paddingTop: 0 }}
                        title={
                            <span style={{ fontWeight: 600 }}>
                                <RetweetOutlined /> Tương tác
                            </span>
                        }
                    >
                        <Descriptions column={1} size="small" bordered={false}>
                            <Descriptions.Item label="Trạng thái">
                                <Tag color={customerStatusColor || undefined}>{customerStatusName}</Tag>
                            </Descriptions.Item>

                            <Descriptions.Item label="Loại tương tác">
                                {customer?.interaction_type?.name || "—"}
                            </Descriptions.Item>

                            <Descriptions.Item label="Trạng thái tương tác">
                                {customer?.interaction_status?.name || "—"}
                            </Descriptions.Item>

                            <Descriptions.Item label="Link facebook">
                                {customer?.fb_link ? (
                                    <a href={customer.fb_link} target="_blank" rel="noopener noreferrer">
                                        {customer.fb_link}
                                    </a>
                                ) : (
                                    "—"
                                )}
                            </Descriptions.Item>

                            <Descriptions.Item label="Link fanpage">
                                {customer?.fb_source ? (
                                    <a href={customer.fb_source} target="_blank" rel="noopener noreferrer">
                                        {customer.fb_source}
                                    </a>
                                ) : (
                                    "—"
                                )}
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>

                    {/* RELATIONSHIPS */}
                    <Card
                        size="small"
                        bordered
                        style={{ marginBottom: 16, paddingTop: 0 }}
                        bodyStyle={{ padding: 0 }}
                        title={
                            <span style={{ fontWeight: 600 }}>
                                <UsergroupAddOutlined /> Thông tin người liên quan
                            </span>
                        }
                    >
                        <CustomerRelationshipsTable data={relationshipList} />
                    </Card>
                    {/* CARD INFO */}
                    <Card
                        size="small"
                        title={
                            <>
                                <CreditCardOutlined /> Mã định danh & Thẻ
                            </>
                        }
                        bordered
                        style={{ marginBottom: 16 }}
                    >
                        <Descriptions column={1} size="small" bordered={false}>
                            <Descriptions.Item label="Mã số thẻ cũ">
                                {customer?.card_number ? (
                                    <Paragraph
                                        style={{ margin: 0 }}
                                        copyable={{
                                            text: customer?.card_number ?? null,
                                            tooltips: ["Sao chép mã", "Đã sao chép!"],
                                        }}
                                    >
                                        {customer?.card_number}
                                    </Paragraph>
                                ) : (
                                    "-"
                                )}
                            </Descriptions.Item>

                            <Descriptions.Item label="Mã số thẻ mới">
                                {customer?.card_number_new ? (
                                    <Paragraph
                                        style={{ margin: 0 }}
                                        copyable={{
                                            text: customer?.card_number_new ?? null,
                                            tooltips: ["Sao chép mã", "Đã sao chép!"],
                                        }}
                                    >
                                        {customer?.card_number_new}
                                    </Paragraph>
                                ) : (
                                    "-"
                                )}
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>
                </Col>

                <Col xs={24} md={24} lg={10}>
                    {/* FINANCE */}
                    <Card
                        size="small"
                        title={
                            <span style={{ display: "inline-flex", alignItems: "center" }}>
                                <DollarOutlined />
                                <span style={{ marginLeft: 4 }}>Thông tin tài chính</span>
                                <Tooltip
                                    placement="top"
                                    title={
                                        <span>
                                            Tổng doanh số, Đã thanh toán và Còn nợ đều phản ánh riêng phần dịch vụ và sản phẩm, không cộng trùng giữa các Phiếu thu.
                                        </span>
                                    }
                                >
                                    <QuestionCircleOutlined
                                        style={{ marginLeft: 6, color: "#8c8c8c", cursor: "help" }}
                                    />
                                </Tooltip>
                            </span>
                        }
                        bordered
                        style={{ marginBottom: 16 }}
                        extra={
                            canViewFinance && (
                                <Button
                                    type="text"
                                    size="small"
                                    icon={isFinanceVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                                    onClick={() => setIsFinanceVisible(!isFinanceVisible)}
                                >
                                    {isFinanceVisible ? "Ẩn" : "Hiện"}
                                </Button>
                            )
                        }
                    >
                        <Descriptions column={1} size="small" bordered={false}>
                            <Descriptions.Item label={<FinanceLabel
                                label="Tổng doanh số"
                                description="Doanh số dịch vụ sau ưu đãi cộng giá trị sản phẩm đã bán."
                            />}>
                                <Tag color="green">
                                    {isFinanceVisible ? (
                                        <span style={{ fontWeight: 600, fontSize: 16 }}>{renderMoney(totalRevenue)}</span>
                                    ) : "••••••••"}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label={<FinanceLabel
                                label="Đã thanh toán"
                                description="Tiền đã dùng thanh toán dịch vụ cộng tiền sản phẩm đã thanh toán. Không gồm số dư hoặc tiền cọc chưa sử dụng."
                            />}>
                                <Tag color="cyan">
                                    {isFinanceVisible ? (
                                        <span style={{ fontWeight: 600, fontSize: 16 }}>{renderMoney(totalPaid)}</span>
                                    ) : "••••••••"}
                                </Tag>
                            </Descriptions.Item>
                            {canReconcileDebt && (
                                <Descriptions.Item label="">
                                    <Tooltip
                                        placement="topLeft"
                                        title="Một số dữ liệu trước đây được nhập khi hệ thống chưa chuẩn hóa đầy đủ, dẫn đến thiếu liên kết giữa phiếu thu và dịch vụ/sản phẩm, có phiếu thu nhưng chưa ghi nhận dịch vụ hoặc giá trị thanh toán không khớp với nghĩa vụ thực tế. Điều này có thể khiến số tiền phân bổ và công nợ chưa chính xác. Chức năng này giúp kiểm tra, đối chiếu và cập nhật công nợ đối với các trường hợp đủ điều kiện."
                                    >
                                        <Button
                                            type="primary"
                                            size="small"
                                            icon={<ReloadOutlined />}
                                            loading={isReconcilingDebt}
                                            disabled={isReconcilingDebt}
                                            onClick={handleReconcileDebt}
                                        >
                                            Cập nhật công nợ
                                        </Button>
                                    </Tooltip>
                                </Descriptions.Item>
                            )}
                            <Descriptions.Item label={<FinanceLabel
                                label="Còn nợ"
                                description="Số tiền khách còn phải thanh toán."
                            />}>
                                <Tag color="red">
                                    {isFinanceVisible ? (
                                        <span style={{ fontWeight: 600, fontSize: 16 }}>{renderMoney(totalDebt)}</span>
                                    ) : "••••••••"}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label={<FinanceLabel
                                label="Tỷ lệ thanh toán"
                                description="Đã thanh toán chia cho Tổng doanh số."
                            />}>
                                <Tag color={collectRateColor}>
                                    {isFinanceVisible ? (
                                        <span style={{ fontWeight: 600, fontSize: 16 }}>{paidPercent}%</span>
                                    ) : "•••"}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label={<FinanceLabel
                                label="Dịch vụ chưa làm"
                                description="Tổng thành tiền của các dịch vụ đã chốt đang ở trạng thái Chưa làm."
                            />}>
                                <Tag color="gold">
                                    {isFinanceVisible ? (
                                        <span style={{ fontWeight: 600, fontSize: 16 }}>
                                            {renderMoney(customer?.pending_service_revenue)}
                                        </span>
                                    ) : (
                                        "••••••••"
                                    )}
                                </Tag>
                            </Descriptions.Item>

                            <Descriptions.Item label={<FinanceLabel
                                label="Chi phí bổ sung"
                                description="Tổng các khoản đã được nhập tại mục Chi phí bổ sung của khách. Bấm biểu tượng bút để xem hoặc cập nhật."
                            />}>
                                <Tooltip placement="topLeft" title="Click để cập nhật">
                                    <span className="item-action-btn" onClick={() => setOpenAdditionalCostModal(true)}>
                                        <Tag color="orange">
                                            {isFinanceVisible ? (
                                                <>
                                                    <span style={{ fontWeight: 600, fontSize: 16 }}>
                                                        {renderMoney(customer?.additional_cost_total)}
                                                    </span>
                                                </>
                                            ) : (
                                                "••••••••"
                                            )}
                                        </Tag>
                                        <EditOutlined />
                                    </span>
                                </Tooltip>
                            </Descriptions.Item>

                            <Descriptions.Item label={<FinanceLabel
                                label="Hình thức thanh toán"
                                description="Lấy theo thiết lập hình thức thanh toán gần nhất của khách. Số tiền/tháng là mức trả hàng tháng đã khai báo."
                            />}>
                                <Tooltip placement="topLeft" title="Click để cập nhật">
                                    <span className="item-action-btn" onClick={() => setOpenPaymentMethodModal(true)}>
                                        {customer?.latest_customer_payment?.payment_method && (
                                            <>
                                                {renderPaymentMethods(customer?.latest_customer_payment?.payment_method)}
                                                {isFinanceVisible && (
                                                    <>
                                                        {renderMoney(customer?.latest_customer_payment?.monthly_payment)}
                                                        /tháng&nbsp;
                                                    </>
                                                )}
                                            </>
                                        )}
                                        <EditOutlined />
                                    </span>
                                </Tooltip>
                            </Descriptions.Item>
                        </Descriptions>

                        <Divider orientation="left" style={{ margin: '12px 0' }} orientationMargin={0}><strong>Chi tiết thanh toán</strong></Divider>

                        <Descriptions column={1} size="small" bordered={false}>
                            <Descriptions.Item
                                label={<FinanceLabel
                                    label="Doanh số dịch vụ"
                                    description="Tổng giá trị các dịch vụ của khách sau ưu đãi. Không bao gồm tiền sản phẩm."
                                />}
                            >
                                <Tag color="green">
                                    {isFinanceVisible ? (
                                        <span style={{ fontWeight: 600, fontSize: 16 }}>
                                            {renderMoney(customer?.revenue)}
                                        </span>
                                    ) : (
                                        "••••••••"
                                    )}
                                </Tag>
                            </Descriptions.Item>

                            <Descriptions.Item
                                label={<FinanceLabel
                                    label="Sản phẩm còn phải thanh toán"
                                    description="Phần giá trị sản phẩm đã bán nhưng chưa được thanh toán."
                                />}
                            >
                                <Tag color="orange">{isFinanceVisible ? (
                                    <span style={{ fontWeight: 600, fontSize: 16 }}>
                                        {renderMoney(productOutstanding)}
                                    </span>
                                ) : "••••••••"}</Tag>
                            </Descriptions.Item>

                            <Descriptions.Item
                                label={<FinanceLabel
                                    label="Tiền sản phẩm đã thanh toán"
                                    description="Tổng tiền các sản phẩm khách đã mua và thanh toán. Không tính vào doanh số và công nợ dịch vụ."
                                />}
                            >
                                <Tag color="blue">
                                    {isFinanceVisible ? (
                                        <Button
                                            type="link"
                                            disabled={productPaymentItems.length === 0}
                                            onClick={() => setOpenProductPaymentModal(true)}
                                            style={{ padding: 0, height: "auto", fontWeight: 600, fontSize: 16 }}
                                        >
                                            {renderMoney(customer?.product_paid_amount)}
                                        </Button>
                                    ) : (
                                        "••••••••"
                                    )}
                                </Tag>
                            </Descriptions.Item>

                            <Descriptions.Item
                                label={<FinanceLabel
                                    label="Ưu đãi"
                                    description="Tổng tiền ưu đãi, giảm giá đã ghi nhận cho dịch vụ."
                                />}
                            >
                                <Tag color="geekblue">
                                    {isFinanceVisible ? (
                                        <span style={{ fontWeight: 600, fontSize: 16 }}>
                                            {renderMoney(customer?.total_discount)}
                                        </span>
                                    ) : (
                                        "••••••••"
                                    )}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label={<FinanceLabel
                                label="Đã thanh toán cho dịch vụ"
                                description="Tổng tiền đã dùng để thanh toán dịch vụ, gồm tiền dịch vụ trong phiếu thu và số dư hoặc tiền cọc đã sử dụng. Không gồm tiền sản phẩm."
                            />}>
                                <Tag color="cyan">
                                    {isFinanceVisible ? (
                                        <span style={{ fontWeight: 600, fontSize: 16 }}>
                                            {renderMoney(customer?.allocated_amount)}
                                        </span>
                                    ) : "••••••••"}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={
                                    <FinanceLabel
                                        label="Số dư của khách hàng"
                                        description="Tiền của khách chưa dùng cho dịch vụ, như tiền dịch vụ nộp thừa, tiền trả lại khi điều chỉnh dịch vụ hoặc tiền được chuyển từ khách khác. Không gồm tiền sản phẩm và tiền cọc."
                                    />
                                }
                            >
                                <Tag color="gold">
                                    {isFinanceVisible ? (
                                        <span style={{ fontWeight: 600, fontSize: 16 }}>
                                            {renderMoney(customer?.unallocated_balance)}
                                        </span>
                                    ) : "••••••••"}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label={<FinanceLabel
                                label="Tiền cọc còn lại"
                                description="Tiền khách đã đặt cọc còn chưa dùng cho dịch vụ hoặc chưa hoàn lại."
                            />}>
                                <Tag color="purple">
                                    {isFinanceVisible ? (
                                        <span style={{ fontWeight: 600, fontSize: 16 }}>
                                            {renderMoney(customer?.deposit_balance)}
                                        </span>
                                    ) : "••••••••"}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label={<FinanceLabel
                                label="Đã chuyển cho khách khác"
                                description="Tổng số tiền từ số dư hoặc tiền cọc của khách đã chuyển sang khách hàng khác."
                            />}>
                                <Tag color="orange">
                                    {isFinanceVisible ? (
                                        <span style={{ fontWeight: 600, fontSize: 16 }}>
                                            {renderMoney(customer?.transferred_to_other_customers_amount)}
                                        </span>
                                    ) : "••••••••"}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label={<FinanceLabel
                                label="Đã nhận từ khách khác"
                                description="Tổng số tiền khách đã nhận từ số dư hoặc tiền cọc của khách hàng khác."
                            />}>
                                <Tag color="lime">
                                    {isFinanceVisible ? (
                                        <span style={{ fontWeight: 600, fontSize: 16 }}>
                                            {renderMoney(customer?.received_from_other_customers_amount)}
                                        </span>
                                    ) : "••••••••"}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label={<FinanceLabel
                                label="Tổng tiền khách đã nộp"
                                description="Tổng tiền khách đã thực nộp qua các phiếu thu, gồm tiền sản phẩm, tiền dịch vụ và tiền nhận cọc. Một phần có thể vẫn ở số dư hoặc tiền cọc."
                            />}>
                                <Tag color="magenta">
                                    {isFinanceVisible ? (
                                        <span style={{ fontWeight: 600, fontSize: 16 }}>
                                            {renderMoney(customer?.total_amount_paid)}
                                        </span>
                                    ) : (
                                        "••••••••"
                                    )}
                                </Tag>
                            </Descriptions.Item>

                            <Descriptions.Item label={<FinanceLabel
                                label="Đã hoàn cho khách"
                                description="Tổng tiền đã hoàn lại cho khách, có thể từ tiền sản phẩm, tiền dịch vụ, số dư hoặc tiền cọc."
                            />}>
                                <Tag color="volcano">
                                    {isFinanceVisible ? (
                                        <span style={{ fontWeight: 600, fontSize: 16 }}>
                                            {renderMoney(customer?.refunded_amount)}
                                        </span>
                                    ) : "••••••••"}
                                </Tag>
                            </Descriptions.Item>
                            {canUpdateCustomer && canViewFinance && (
                                <Descriptions.Item label="">
                                    <Tooltip
                                        placement="topLeft"
                                        title="Một số dữ liệu tài chính trước đây được nhập khi hệ thống chưa chuẩn hóa đầy đủ, dẫn đến thiếu thông tin liên kết giữa phiếu thu, hoàn tiền và phân bổ thanh toán, gây sai lệch số dư hoặc công nợ. Chức năng này sẽ kiểm tra, đối chiếu và đề xuất phương án xử lý có kiểm soát trước khi cập nhật dữ liệu."
                                    >
                                        <Button
                                            size="small"
                                            icon={<RetweetOutlined />}
                                            loading={isLoadingFinancialRepair && !financialRepairPreview}
                                            disabled={isLoadingFinancialRepair}
                                            onClick={openFinancialRepair}
                                            style={{
                                                color: '#096dd9',
                                                borderColor: '#69c0ff',
                                                background: '#e6f7ff',
                                            }}
                                        >
                                            Xử lý sai lệch tài chính
                                        </Button>
                                    </Tooltip>
                                </Descriptions.Item>
                            )}
                        </Descriptions>

                        {(checkPermission('customer_refund', 'view') || checkPermission('customer_refund', 'execute') || checkPermission('customer_deposit', 'view')) && <>
                            <Divider orientation="left" orientationMargin={0} style={{ margin: '12px 0' }}>Quản lý tiền khách hàng</Divider>
                            <CustomerFinanceActions customerId={customerId} onChanged={handleRefreshCustomer} />
                        </>}
                    </Card>
                </Col>
                <Col span={24}>
                    <div style={{ fontStyle: "italic" }}>
                        <CreatedAtCell
                            createdAt={customer?.created_at}
                            user={customer?.created_user}
                            layout="horizontal"
                            label="Ngày tạo"
                        />
                    </div>
                </Col>
            </Row>

            <Modal
                visible={Boolean(financialRepairPreview)}
                title="Xử lý sai lệch tài chính khách hàng"
                maskClosable={false}
                width={isMobile ? "100%" : "85%"}
                onCancel={() => {
                    setFinancialRepairPreview(null);
                    setFinancialRepairResult(null);
                    setFinancialRepairReason("");
                }}
                footer={[
                    <Button key="close" onClick={() => {
                        setFinancialRepairPreview(null);
                        setFinancialRepairResult(null);
                        setFinancialRepairReason("");
                    }}>
                        Đóng
                    </Button>,
                    <Button
                        key="execute"
                        type="primary"
                        loading={isLoadingFinancialRepair}
                        disabled={Boolean(financialRepairResult)
                            || !financialRepairPreview?.can_execute
                            || !Array.isArray(financialRepairPreview?.planned_actions)
                            || financialRepairPreview.planned_actions.length === 0
                            || financialRepairReason.trim().length < 5}
                        onClick={executeFinancialRepair}
                    >
                        Xác nhận xử lý
                    </Button>
                ]}
            >
                <Alert
                    type={getFinancialRepairAlertType(financialRepairDisplayStatus)}
                    message={financialRepairResult?.message || financialRepairPreview?.message}
                    style={{ marginBottom: 16 }}
                />

                <Descriptions bordered size="small" column={isMobile ? 1 : 3} style={{ marginBottom: 16 }}>
                    <Descriptions.Item label="Nguồn khả dụng">
                        {renderMoney(financialRepairPreview?.before?.reconciliation?.available_funds || 0)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Đã phân bổ">
                        {renderMoney(financialRepairPreview?.before?.reconciliation?.allocated_total || 0)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Chênh lệch nguồn">
                        {renderMoney(financialRepairPreview?.before?.reconciliation?.funding_difference || 0)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Công nợ hiện tại">
                        {renderMoney(financialRepairPreview?.before?.reconciliation?.debt || 0)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Công nợ kỳ vọng">
                        {renderMoney(financialRepairPreview?.before?.reconciliation?.expected_debt || 0)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Trạng thái">
                        <Tag color={getFinancialRepairStatusColor(financialRepairDisplayStatus)}>
                            {formatFinancialRepairStatus(financialRepairDisplayStatus)}
                        </Tag>
                    </Descriptions.Item>
                </Descriptions>

                {financialRepairResult?.after?.reconciliation && <>
                    <Text strong>Kết quả sau xử lý</Text>
                    <Descriptions bordered size="small" column={isMobile ? 1 : 3} style={{ margin: "8px 0 16px" }}>
                        <Descriptions.Item label="Nguồn khả dụng">
                            {renderMoney(financialRepairResult.after.reconciliation.available_funds || 0)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Đã phân bổ">
                            {renderMoney(financialRepairResult.after.reconciliation.allocated_total || 0)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Chênh lệch nguồn">
                            {renderMoney(financialRepairResult.after.reconciliation.funding_difference || 0)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Công nợ sau xử lý">
                            {renderMoney(financialRepairResult.after.reconciliation.debt || 0)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Công nợ kỳ vọng">
                            {renderMoney(financialRepairResult.after.reconciliation.expected_debt || 0)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Mã lần xử lý">
                            {financialRepairResult.operation_id || "—"}
                        </Descriptions.Item>
                    </Descriptions>
                </>}

                {Array.isArray(financialRepairPreview?.issues) && financialRepairPreview.issues.length > 0 && <>
                    <Text strong>Sai lệch phát hiện</Text>
                    <Table
                        rowKey={(row, index) => `${row.code}-${row.receipt_id || index}`}
                        size="small"
                        pagination={false}
                        dataSource={financialRepairPreview.issues}
                        style={{ margin: "8px 0 16px" }}
                        columns={[
                            { title: "Phân loại", dataIndex: "code", width: 230, render: formatFinancialRepairIssue },
                            { title: "Phiếu", dataIndex: "receipt_id", width: 90, render: value => value || "—" },
                            { title: "Số tiền", dataIndex: "amount", width: 130, align: "right", render: value => value ? renderMoney(value) : "—" },
                            { title: "Mô tả", dataIndex: "message" }
                        ]}
                    />
                </>}

                {Array.isArray(financialRepairPreview?.blockers) && financialRepairPreview.blockers.length > 0 && <div style={{ marginBottom: 16 }}>
                    <Text strong>Cần can thiệp có kiểm soát</Text>
                    <ul style={{ marginTop: 8, paddingLeft: 24 }}>
                        {financialRepairPreview.blockers.map((item, index) => (
                            <li key={`${item.code}-${index}`}>
                                <Text strong>{formatFinancialRepairBlocker(item.code)}</Text>: {item.message}
                            </li>
                        ))}
                    </ul>
                </div>}

                {Array.isArray(financialRepairPreview?.planned_actions) && financialRepairPreview.planned_actions.length > 0 && <>
                    <Text strong>Thay đổi dự kiến</Text>
                    <Table
                        rowKey="action_key"
                        size="small"
                        pagination={false}
                        dataSource={financialRepairPreview.planned_actions}
                        style={{ margin: "8px 0 16px" }}
                        columns={[
                            { title: "Hành động", dataIndex: "action_type", width: 240, render: formatFinancialRepairAction },
                            { title: "Phiếu", dataIndex: "receipt_id", width: 90, render: value => value || "—" },
                            { title: "Ảnh hưởng công nợ", dataIndex: "direction", width: 150, render: formatFinancialRepairDirection },
                            { title: "Số tiền", dataIndex: "amount", width: 130, align: "right", render: renderMoney },
                            { title: "Căn cứ", dataIndex: "reason" }
                        ]}
                    />
                </>}

                {!financialRepairResult && financialRepairPreview?.can_execute && financialRepairPreview?.planned_actions?.length > 0 && <>
                    <Divider orientation="left" orientationMargin={0}>Xác nhận thực hiện</Divider>
                    <div style={{ marginBottom: 12 }}>
                        <Text strong>Chế độ</Text>
                        <Select
                            value={financialRepairMode}
                            onChange={setFinancialRepairMode}
                            style={{ width: "100%", marginTop: 6 }}
                        >
                            {financialRepairModeOptions.map(option => (
                                <Select.Option key={option.value} value={option.value}>{option.label}</Select.Option>
                            ))}
                        </Select>
                    </div>
                    <div>
                        <Text strong>Lý do xử lý</Text>
                        <Input.TextArea
                            value={financialRepairReason}
                            onChange={event => setFinancialRepairReason(event.target.value)}
                            rows={3}
                            maxLength={1000}
                            placeholder="Nhập căn cứ kiểm tra (tối thiểu 5 ký tự)"
                            style={{ marginTop: 6 }}
                        />
                    </div>
                </>}
            </Modal>

            <Modal
                visible={Boolean(debtReconciliationBlocker)}
                title={debtReconciliationBlocker?.block_code === "INVALID_RECEIPT_ITEM_MAPPING"
                    ? "Dữ liệu phiếu thu cần được kiểm tra"
                    : "Chưa thể tự động cập nhật công nợ"}
                footer={[
                    <Button key="close" type="primary" onClick={() => setDebtReconciliationBlocker(null)}>
                        Đóng
                    </Button>
                ]}
                maskClosable={false}
                width={isMobile ? "100%" : "85%"}
                onCancel={() => setDebtReconciliationBlocker(null)}
            >
                <Paragraph>
                    {debtReconciliationBlocker?.message || "Hệ thống chưa cập nhật công nợ vì dữ liệu tài chính còn mâu thuẫn."}
                </Paragraph>
                {Array.isArray(debtReconciliationBlocker?.required_actions) && debtReconciliationBlocker.required_actions.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                        <Text strong>Thứ tự xử lý</Text>
                        <ol style={{ marginTop: 8, paddingLeft: 24 }}>
                            {debtReconciliationBlocker.required_actions.map((action, index) => (
                                <li key={`${index}-${action}`}>{action}</li>
                            ))}
                        </ol>
                    </div>
                )}
                {Array.isArray(debtReconciliationBlocker?.conflicts) && debtReconciliationBlocker.conflicts.length > 0 && (
                    <Descriptions bordered size="small" column={1} style={{ marginBottom: 16 }}>
                        {Object.entries(debtReconciliationBlocker.conflicts[0]).map(([key, value]) => (
                            <Descriptions.Item key={key} label={debtConflictLabels[key] || "Thông tin liên quan"}>
                                {typeof value === "number" && !key.endsWith("_id") ? renderMoney(value) : (value || "—")}
                            </Descriptions.Item>
                        ))}
                    </Descriptions>
                )}
                {Array.isArray(debtReconciliationBlocker?.invalid_items) && debtReconciliationBlocker.invalid_items.length > 0 && <Descriptions bordered size="small" column={1} style={{ marginBottom: 16 }}>
                    <Descriptions.Item label="Mã dòng phiếu thu">
                        <Text copyable={{ text: (debtReconciliationBlocker?.invalid_receipt_item_ids || []).join(", ") }}>
                            {(debtReconciliationBlocker?.invalid_receipt_item_ids || []).join(", ") || "—"}
                        </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Mã dịch vụ khách hàng">
                        <Text copyable={{ text: (debtReconciliationBlocker?.invalid_customer_service_ids || []).join(", ") }}>
                            {(debtReconciliationBlocker?.invalid_customer_service_ids || []).join(", ") || "—"}
                        </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Mã dịch vụ gốc">
                        <Text copyable={{ text: (debtReconciliationBlocker?.invalid_origin_ids || []).join(", ") }}>
                            {(debtReconciliationBlocker?.invalid_origin_ids || []).join(", ") || "—"}
                        </Text>
                    </Descriptions.Item>
                </Descriptions>}
                {Array.isArray(debtReconciliationBlocker?.invalid_items) && debtReconciliationBlocker.invalid_items.length > 0 && <Table
                    rowKey="receipt_item_id"
                    size="small"
                    dataSource={debtReconciliationBlocker?.invalid_items || []}
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 1000 }}
                    columns={[
                        { title: "Mã dòng phiếu", dataIndex: "receipt_item_id", width: 130 },
                        { title: "Mã phiếu", dataIndex: "receipt_id", width: 110 },
                        { title: "Mã khách", dataIndex: "customer_code", width: 120, render: value => value || "—" },
                        { title: "Mã dịch vụ khách", dataIndex: "customer_service_id", width: 160 },
                        { title: "Mã dịch vụ gốc", dataIndex: "origin_id", width: 130 },
                        { title: "Loại", dataIndex: "type", width: 110, render: formatReceiptItemType },
                        { title: "Số tiền", dataIndex: "amount", width: 130, align: "right", render: renderMoney },
                        {
                            title: "Lý do",
                            dataIndex: "reason",
                            width: 240,
                            render: formatReceiptItemIssueReason
                        }
                    ]}
                />}
            </Modal>

            <Modal
                visible={openProductPaymentModal}
                title="Chi tiết sản phẩm đã thanh toán"
                footer={null}
                maskClosable={false}
                width={isMobile ? "100%" : "85%"}
                onCancel={() => setOpenProductPaymentModal(false)}
            >
                <Paragraph type="secondary">
                    Các dòng dưới đây được lấy từ phần sản phẩm trên các phiếu thu của khách hàng. Chúng được hiển thị tại đây để đối chiếu những sản phẩm đã thu tiền và xác định phần tiền sản phẩm còn có thể hoàn. Số tổng là tiền sản phẩm đã thanh toán sau khi trừ các khoản hoàn từ tiền sản phẩm đã hoàn tất. Sản phẩm 0 đồng vẫn được hiển thị để đối chiếu đầy đủ nội dung phiếu thu.
                </Paragraph>
                <Table
                    rowKey="id"
                    size="small"
                    dataSource={productPaymentItems}
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 1120 }}
                    columns={[
                        {
                            title: "Ngày thu",
                            dataIndex: "payment_at",
                            width: 150,
                            render: value => value ? moment(value).format("DD/MM/YYYY HH:mm") : "—"
                        },
                        { title: "Mã phiếu", dataIndex: "receipt_code", width: 140 },
                        { title: "Sản phẩm", dataIndex: "name", width: 220 },
                        { title: "Số lượng", dataIndex: "quantity", align: "right", width: 100 },
                        {
                            title: "Giá bán",
                            dataIndex: "price",
                            align: "right",
                            width: 130,
                            render: renderMoney
                        },
                        {
                            title: "Thành tiền",
                            dataIndex: "amount",
                            align: "right",
                            width: 140,
                            render: renderMoney
                        },
                        {
                            title: "Đã hoàn",
                            dataIndex: "product_refunded_amount",
                            align: "right",
                            width: 130,
                            render: renderMoney
                        },
                        {
                            title: "Còn hiệu lực",
                            dataIndex: "product_paid_amount",
                            align: "right",
                            width: 140,
                            render: renderMoney
                        },
                        {
                            title: "Trạng thái hoàn",
                            dataIndex: "refund_status",
                            width: 150,
                            render: value => {
                                if (value === "FULL") return <Tag color="red">Đã hoàn</Tag>;
                                if (value === "PARTIAL") return <Tag color="orange">Hoàn một phần</Tag>;
                                return null;
                            }
                        }
                    ]}
                />
            </Modal>

            <CustomerEditForm
                visible={isEditModalOpen}
                currentCustomer={customer}
                reloadData={handleRefreshCustomer}
                onCancel={closeEditModal}
            />
            <CustomerPaymentList
                customerId={customerId}
                visible={openPaymentMethodModal}
                onCancel={() => setOpenPaymentMethodModal(false)}
            />
            <AdditionalCost
                customerId={customerId}
                onCancel={() => setOpenAdditionalCostModal(false)}
                visible={openAdditionalCostModal}
            />
            <CustomerPrimaryPhoneModal
                customerId={customerId}
                visible={openPrimaryPhoneModal}
                onCancel={() => setOpenPrimaryPhoneModal(false)}
            />
        </div>
    );
}
